import type {
  AcquisitionRecommendation,
  DraftSlot,
  Player,
  PlayerAnalytics,
  Position,
  RankedPlayer,
  RiskPreferences,
  RosterRequirements,
  SeasonOutlook,
  WeekOutlook,
} from '../types/fantasy-football'
import type { ParamMap } from './fantasy-knowledge-base'

// ─── Analytics engine ────────────────────────────────────────
// Pure functions implementing the knowledge-base formulas (F1–F12 in
// fantasy-knowledge-base.ts). Everything here is deterministic in
// (players, roster, prefs, params, sos) so any output can be reproduced
// from an audit record's parameter snapshot.

export const FANTASY_WEEKS = 17
const GAMES = FANTASY_WEEKS - 1 // one bye week per team

export const ROSTER_REQUIREMENTS: RosterRequirements = {
  QB: 1,
  RB: 2,
  WR: 2,
  TE: 1,
  FLEX: 1,
  K: 1,
  DST: 1,
  bench: 6,
}

const FLEX_ELIGIBLE: Position[] = ['RB', 'WR', 'TE']
const STARTERS_PER_TEAM: Record<Position, number> = { QB: 1, RB: 2.5, WR: 2.5, TE: 1, K: 1, DST: 1 }

/** Per-team-per-position schedule factor; 1 = league average, >1 = softer slate. */
export type SosTable = Record<string, Partial<Record<Position, number>>>

export interface EngineInput {
  pool: Player[]
  rosterIds: string[]
  prefs: RiskPreferences
  params: ParamMap
  sos: SosTable
  leagueTeamCount: number
}

function p(params: ParamMap, key: string, fallback: number): number {
  const v = params[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}

// ─── F1: consensus projection ────────────────────────────────

export function consensusPoints(player: Player, prefs: RiskPreferences): number {
  let weighted = 0
  let total = 0
  for (const proj of player.projections) {
    const w = prefs.sourceWeights[proj.source] ?? 1
    weighted += w * proj.points
    total += w
  }
  return total > 0 ? weighted / total : 0
}

// ─── F2: projection uncertainty ──────────────────────────────

function sourceStdDev(player: Player): number {
  const pts = player.projections.map((s) => s.points)
  if (pts.length < 2) return 0
  const mean = pts.reduce((a, b) => a + b, 0) / pts.length
  const variance = pts.reduce((a, b) => a + (b - mean) ** 2, 0) / (pts.length - 1)
  return Math.sqrt(variance)
}

export function projectionStdDev(player: Player, params: ParamMap): number {
  const k = p(params, 'f2.sourceDisagreementK', 1.5)
  return Math.sqrt(k * sourceStdDev(player) ** 2 + GAMES * player.weeklyStdDev ** 2)
}

// ─── F3 + F4: availability and schedule adjustments ──────────

function availabilityFactor(player: Player, params: ParamMap): number {
  if (player.injuryRisk === 'high') return p(params, 'f3.availHigh', 0.84)
  if (player.injuryRisk === 'medium') return p(params, 'f3.availMedium', 0.92)
  return p(params, 'f3.availLow', 0.97)
}

function scheduleFactor(player: Player, prefs: RiskPreferences, sos: SosTable): number {
  const raw = sos[player.nflTeam]?.[player.position] ?? 1
  return 1 + prefs.scheduleWeight * (raw - 1)
}

export function adjustedPoints(
  player: Player,
  prefs: RiskPreferences,
  params: ParamMap,
  sos: SosTable,
): number {
  return consensusPoints(player, prefs) * availabilityFactor(player, params) * scheduleFactor(player, prefs, sos)
}

/** Weekly scoring mean over the player's active games. */
function weeklyMean(player: Player, prefs: RiskPreferences, params: ParamMap, sos: SosTable): number {
  return adjustedPoints(player, prefs, params, sos) / GAMES
}

// ─── F5: replacement level & VORP ────────────────────────────

export function replacementPoints(
  position: Position,
  pool: Player[],
  prefs: RiskPreferences,
  params: ParamMap,
  sos: SosTable,
  leagueTeamCount: number,
): number {
  const buffers: Partial<Record<Position, string>> = {
    QB: 'f5.streamBufferQB',
    RB: 'f5.streamBufferRB',
    WR: 'f5.streamBufferWR',
    TE: 'f5.streamBufferTE',
  }
  const bufferKey = buffers[position]
  const buffer = bufferKey ? p(params, bufferKey, 2) : 1
  const rank = Math.round(leagueTeamCount * STARTERS_PER_TEAM[position] + buffer)
  const atPos = pool
    .filter((pl) => pl.position === position)
    .map((pl) => adjustedPoints(pl, prefs, params, sos))
    .sort((a, b) => b - a)
  if (atPos.length === 0) return 0
  return atPos[Math.min(rank - 1, atPos.length - 1)]
}

// ─── F6: teammate covariance ─────────────────────────────────

function rho(a: Position, b: Position, params: ParamMap): number {
  const pair = [a, b].sort().join('-')
  switch (pair) {
    case 'QB-WR': return p(params, 'f6.rhoQbWr', 0.35)
    case 'QB-TE': return p(params, 'f6.rhoQbTe', 0.3)
    case 'QB-RB': return p(params, 'f6.rhoQbRb', 0.05)
    case 'RB-RB': return p(params, 'f6.rhoRbRb', -0.25)
    case 'WR-WR': return p(params, 'f6.rhoWrWr', 0.1)
    case 'TE-WR': return p(params, 'f6.rhoWrTe', 0.05)
  }
  if (a === 'K' || b === 'K') return p(params, 'f6.rhoOffK', 0.15)
  if (a === 'DST' || b === 'DST') return p(params, 'f6.rhoOffDst', 0.05)
  return 0
}

/** Marginal weekly covariance the candidate adds against the given roster. */
export function marginalCovariance(
  candidate: Player,
  roster: Player[],
  prefs: RiskPreferences,
  params: ParamMap,
): number {
  let cov = 0
  for (const mate of roster) {
    if (mate.id === candidate.id || mate.nflTeam !== candidate.nflTeam) continue
    cov += rho(candidate.position, mate.position, params) * candidate.weeklyStdDev * mate.weeklyStdDev
  }
  return prefs.teammateCorrelationWeight * cov
}

// ─── F7: bye-week overlap penalty ────────────────────────────

export function byeOverlapPenalty(
  candidate: Player,
  roster: Player[],
  prefs: RiskPreferences,
  params: ParamMap,
  sos: SosTable,
): number {
  const overlap = roster.filter((r) => r.id !== candidate.id && r.byeWeek === candidate.byeWeek).length
  if (overlap === 0) return 0
  const exponent = p(params, 'f7.overlapExponent', 1.5)
  const scale = p(params, 'f7.baseScale', 0.35)
  return prefs.byePenaltyWeight * scale * weeklyMean(candidate, prefs, params, sos) * overlap ** exponent
}

// ─── F8 + F9: risk-adjusted and scarcity-weighted value ──────

function riskAdjustedValue(
  vorp: number,
  seasonStdDev: number,
  weeklyCov: number,
  byePenalty: number,
  prefs: RiskPreferences,
  params: ParamMap,
): number {
  const varScale = p(params, 'f8.varianceScale', 40)
  const covScale = p(params, 'f8.covarianceScale', 10)
  const variancePenalty = prefs.riskAversion * (seasonStdDev ** 2 / GAMES / varScale + (GAMES * weeklyCov) / covScale)
  return vorp - variancePenalty - byePenalty
}

// ─── F10 + F11: weekly win model & expected season wins ──────

function normalCdf(z: number): number {
  // Abramowitz–Stegun erf approximation; |error| < 1.5e-7.
  const t = 1 / (1 + 0.2316419 * Math.abs(z))
  const d = 0.3989423 * Math.exp((-z * z) / 2)
  const prob = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))))
  return z >= 0 ? 1 - prob : prob
}

/** Pick the best weekly starting lineup from a roster (bye-aware). */
function pickStarters(
  roster: Player[],
  week: number,
  prefs: RiskPreferences,
  params: ParamMap,
  sos: SosTable,
): { starters: Player[]; byeOuts: Player[]; emptySlots: Position[] } {
  const active = roster.filter((pl) => pl.byeWeek !== week)
  const byeOuts = roster.filter((pl) => pl.byeWeek === week)
  const byValue = [...active].sort(
    (a, b) => weeklyMean(b, prefs, params, sos) - weeklyMean(a, prefs, params, sos),
  )
  const starters: Player[] = []
  const emptySlots: Position[] = []
  const used = new Set<string>()

  const fill = (position: Position | 'FLEX', count: number) => {
    for (let i = 0; i < count; i++) {
      const pick = byValue.find(
        (pl) =>
          !used.has(pl.id) &&
          (position === 'FLEX' ? FLEX_ELIGIBLE.includes(pl.position) : pl.position === position),
      )
      if (pick) {
        used.add(pick.id)
        starters.push(pick)
      } else {
        emptySlots.push(position === 'FLEX' ? 'WR' : position)
      }
    }
  }

  fill('QB', ROSTER_REQUIREMENTS.QB)
  fill('RB', ROSTER_REQUIREMENTS.RB)
  fill('WR', ROSTER_REQUIREMENTS.WR)
  fill('TE', ROSTER_REQUIREMENTS.TE)
  fill('FLEX', ROSTER_REQUIREMENTS.FLEX)
  fill('K', ROSTER_REQUIREMENTS.K)
  fill('DST', ROSTER_REQUIREMENTS.DST)

  return { starters, byeOuts, emptySlots }
}

function weekOutlook(
  input: EngineInput,
  roster: Player[],
  week: number,
  opponent: string,
  replByPos: Record<Position, number>,
): WeekOutlook {
  const { prefs, params, sos } = input
  const { starters, byeOuts, emptySlots } = pickStarters(roster, week, prefs, params, sos)

  let mean = starters.reduce((sum, pl) => sum + weeklyMean(pl, prefs, params, sos), 0)
  // Empty slots (byes / short rosters) get filled off waivers at replacement level.
  mean += emptySlots.reduce((sum, pos) => sum + replByPos[pos] / GAMES, 0)

  let variance = starters.reduce((sum, pl) => sum + pl.weeklyStdDev ** 2, 0)
  for (let i = 0; i < starters.length; i++) {
    for (let j = i + 1; j < starters.length; j++) {
      if (starters[i].nflTeam === starters[j].nflTeam) {
        variance +=
          2 *
          prefs.teammateCorrelationWeight *
          rho(starters[i].position, starters[j].position, params) *
          starters[i].weeklyStdDev *
          starters[j].weeklyStdDev
      }
    }
  }
  variance = Math.max(variance, 1)

  const oppMean = p(params, 'f10.oppMean', 112)
  const oppStdDev = p(params, 'f10.oppStdDev', 22)
  const winProbability = normalCdf((mean - oppMean) / Math.sqrt(variance + oppStdDev ** 2))

  return {
    week,
    opponent,
    projectedFor: mean,
    projectedAgainst: oppMean,
    rosterStdDev: Math.sqrt(variance),
    winProbability,
    byeOuts: byeOuts.map((pl) => pl.name),
  }
}

export function seasonOutlook(input: EngineInput, rosterOverride?: Player[], opponents?: string[]): SeasonOutlook {
  const roster = rosterOverride ?? input.pool.filter((pl) => input.rosterIds.includes(pl.id))
  const replByPos = replacementByPosition(input)
  const weeks: WeekOutlook[] = []
  for (let w = 1; w <= FANTASY_WEEKS; w++) {
    const opponent = opponents?.[(w - 1) % (opponents.length || 1)] ?? 'League opponent'
    weeks.push(weekOutlook(input, roster, w, opponent, replByPos))
  }
  return {
    expectedWins: weeks.reduce((sum, w) => sum + w.winProbability, 0),
    totalWeeks: FANTASY_WEEKS,
    weeks,
  }
}

function replacementByPosition(input: EngineInput): Record<Position, number> {
  const { pool, prefs, params, sos, leagueTeamCount } = input
  const out = {} as Record<Position, number>
  for (const pos of ['QB', 'RB', 'WR', 'TE', 'K', 'DST'] as Position[]) {
    out[pos] = replacementPoints(pos, pool, prefs, params, sos, leagueTeamCount)
  }
  return out
}

// ─── Full per-player analytics ───────────────────────────────

export function analyzePool(input: EngineInput): RankedPlayer[] {
  const { pool, rosterIds, prefs, params, sos } = input
  const roster = pool.filter((pl) => rosterIds.includes(pl.id))
  const replByPos = replacementByPosition(input)
  const baseline = rosterIds.length > 0 ? seasonOutlook(input).expectedWins : null

  const analyzed = pool.map((player) => {
    const consensus = consensusPoints(player, prefs)
    const stdDev = projectionStdDev(player, params)
    const adjusted = adjustedPoints(player, prefs, params, sos)
    const repl = replByPos[player.position]
    const vorp = adjusted - repl
    const isMine = rosterIds.includes(player.id)
    const covRoster = isMine ? roster.filter((r) => r.id !== player.id) : roster
    const cov = marginalCovariance(player, covRoster, prefs, params)
    const bye = byeOverlapPenalty(player, covRoster, prefs, params, sos)
    const rav = riskAdjustedValue(vorp, stdDev, cov, bye, prefs, params)
    const swv = rav * (prefs.positionScarcity[player.position] ?? 1)

    const notes: string[] = []
    const mates = covRoster.filter((r) => r.nflTeam === player.nflTeam)
    if (mates.length > 0) {
      const dir = cov >= 0 ? 'adds correlated risk' : 'diversifies (negative correlation)'
      notes.push(`Same NFL team as ${mates.map((m) => m.name).join(', ')} — ${dir}`)
    }
    const byeMates = covRoster.filter((r) => r.byeWeek === player.byeWeek)
    if (byeMates.length > 0) {
      notes.push(`Shares Week ${player.byeWeek} bye with ${byeMates.length} rostered player${byeMates.length > 1 ? 's' : ''}`)
    }
    const sched = sos[player.nflTeam]?.[player.position] ?? 1
    if (sched >= 1.05) notes.push('Soft opposing-defense slate boosts projection')
    if (sched <= 0.95) notes.push('Tough opposing-defense slate discounts projection')
    if (player.injuryRisk !== 'low') notes.push(`${player.injuryRisk === 'high' ? 'High' : 'Medium'} injury risk — availability discounted`)

    const analytics: PlayerAnalytics = {
      playerId: player.id,
      consensusPoints: consensus,
      projectionStdDev: stdDev,
      adjustedPoints: adjusted,
      replacementPoints: repl,
      vorp,
      rosterCovariance: cov,
      byeOverlapPenalty: bye,
      riskAdjustedValue: rav,
      scarcityWeightedValue: swv,
      expectedWinsDelta: 0, // filled below for available players
      notes,
    }
    return { player, analytics, rank: 0 }
  })

  // F11: ΔWins for available players — swap in for the weakest same-position
  // roster player (or add outright if the roster has room).
  if (baseline !== null) {
    for (const entry of analyzed) {
      if (entry.player.rosterStatus !== 'available') continue
      const samePos = roster
        .filter((r) => r.position === entry.player.position)
        .sort((a, b) => adjustedPoints(a, prefs, params, sos) - adjustedPoints(b, prefs, params, sos))
      const drop = roster.length >= rosterCapacity() ? samePos[0] : undefined
      const trial = drop
        ? [...roster.filter((r) => r.id !== drop.id), entry.player]
        : [...roster, entry.player]
      entry.analytics.expectedWinsDelta = seasonOutlook(input, trial).expectedWins - baseline
    }
  }

  analyzed.sort((a, b) => b.analytics.scarcityWeightedValue - a.analytics.scarcityWeightedValue)
  analyzed.forEach((entry, i) => (entry.rank = i + 1))
  return analyzed
}

export function rosterCapacity(): number {
  const r = ROSTER_REQUIREMENTS
  return r.QB + r.RB + r.WR + r.TE + r.FLEX + r.K + r.DST + r.bench
}

// ─── F11: acquisition recommendations ────────────────────────

export function recommendAcquisitions(input: EngineInput, limit = 5): AcquisitionRecommendation[] {
  const { pool, rosterIds, prefs, params, sos } = input
  if (rosterIds.length === 0) return []
  const roster = pool.filter((pl) => rosterIds.includes(pl.id))
  const ranked = analyzePool(input)
  const full = roster.length >= rosterCapacity()

  return ranked
    .filter((e) => e.player.rosterStatus === 'available' && e.analytics.expectedWinsDelta > 0.005)
    .slice(0, 25)
    .sort((a, b) => b.analytics.expectedWinsDelta - a.analytics.expectedWinsDelta)
    .slice(0, limit)
    .map((e) => {
      const samePos = roster
        .filter((r) => r.position === e.player.position)
        .sort((a, b) => adjustedPoints(a, prefs, params, sos) - adjustedPoints(b, prefs, params, sos))
      const drop = full ? samePos[0] ?? null : null
      const myRav = ranked.find((r) => r.player.id === drop?.id)?.analytics.riskAdjustedValue ?? 0
      return {
        playerId: e.player.id,
        playerName: e.player.name,
        position: e.player.position,
        dropPlayerId: drop?.id ?? null,
        dropPlayerName: drop?.name ?? null,
        deltaExpectedWins: e.analytics.expectedWinsDelta,
        deltaRiskAdjustedValue: e.analytics.riskAdjustedValue - myRav,
        reason: buildAcquisitionReason(e),
      }
    })
}

function buildAcquisitionReason(e: RankedPlayer): string {
  const parts = [
    `+${e.analytics.expectedWinsDelta.toFixed(2)} expected wins`,
    `VORP ${e.analytics.vorp.toFixed(0)}`,
  ]
  if (e.analytics.rosterCovariance > 0.5) parts.push('note: adds teammate correlation')
  if (e.analytics.rosterCovariance < -0.5) parts.push('diversifies roster risk')
  if (e.analytics.byeOverlapPenalty > 1) parts.push('note: bye-week overlap')
  return parts.join(' · ')
}

// ─── F12: draft order construction ───────────────────────────

export function generateDraftOrder(input: EngineInput, boardSize = 60): DraftSlot[] {
  const { pool, prefs, params, sos } = input
  const simulated: Player[] = []
  const remaining = [...pool]
  const slots: DraftSlot[] = []

  const openSlots = (): Record<Position | 'FLEX' | 'bench', number> => {
    const counts: Record<Position, number> = { QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DST: 0 }
    for (const pl of simulated) counts[pl.position]++
    const r = ROSTER_REQUIREMENTS
    const open = {
      QB: Math.max(0, r.QB - counts.QB),
      RB: Math.max(0, r.RB - counts.RB),
      WR: Math.max(0, r.WR - counts.WR),
      TE: Math.max(0, r.TE - counts.TE),
      K: Math.max(0, r.K - counts.K),
      DST: Math.max(0, r.DST - counts.DST),
      FLEX: 0,
      bench: 0,
    }
    const flexUsed = Math.max(0, counts.RB - r.RB) + Math.max(0, counts.WR - r.WR) + Math.max(0, counts.TE - r.TE)
    open.FLEX = Math.max(0, r.FLEX - flexUsed)
    open.bench = Math.max(0, rosterCapacity() - simulated.length - open.QB - open.RB - open.WR - open.TE - open.FLEX - open.K - open.DST)
    return open
  }

  const needMultiplier = (pos: Position): number => {
    const open = openSlots()
    const starterNeed = p(params, 'f12.needOpenStarter', 1)
    const flexNeed = p(params, 'f12.needFlex', 0.85)
    const benchNeed = p(params, 'f12.needBench', 0.55)
    const kdstDamper = p(params, 'f12.lateRoundKDst', 0.35)
    const coreOpen = open.QB + open.RB + open.WR + open.TE + open.FLEX > 0
    if ((pos === 'K' || pos === 'DST') && coreOpen) {
      return (open[pos] > 0 ? starterNeed : benchNeed) * kdstDamper
    }
    if (open[pos] > 0) return starterNeed
    if (FLEX_ELIGIBLE.includes(pos) && open.FLEX > 0) return flexNeed
    return benchNeed
  }

  const size = Math.min(boardSize, pool.length)
  for (let pickNo = 1; pickNo <= size; pickNo++) {
    let best: { player: Player; score: number; reason: string } | null = null
    const inRosterPhase = simulated.length < rosterCapacity()

    for (const candidate of remaining) {
      const consensus = adjustedPoints(candidate, prefs, params, sos)
      const repl = replacementPoints(candidate.position, pool, prefs, params, sos, input.leagueTeamCount)
      const vorp = consensus - repl
      const cov = marginalCovariance(candidate, simulated, prefs, params)
      const bye = byeOverlapPenalty(candidate, simulated, prefs, params, sos)
      const rav = riskAdjustedValue(vorp, projectionStdDev(candidate, params), cov, bye, prefs, params)
      const swv = rav * (prefs.positionScarcity[candidate.position] ?? 1)
      const score = inRosterPhase ? swv * needMultiplier(candidate.position) : swv

      if (!best || score > best.score) {
        const reasonBits = [`SWV ${swv.toFixed(1)}`]
        if (bye > 1) reasonBits.push(`bye overlap −${bye.toFixed(1)}`)
        if (Math.abs(cov) > 0.5) reasonBits.push(cov > 0 ? 'teammate corr.' : 'diversifying')
        best = { player: candidate, score, reason: reasonBits.join(' · ') }
      }
    }
    if (!best) break

    slots.push({ overallRank: pickNo, playerId: best.player.id, score: best.score, reason: best.reason })
    if (inRosterPhase) simulated.push(best.player)
    remaining.splice(remaining.indexOf(best.player), 1)
  }
  return slots
}
